// src/components/floorplans/FloorplanCalibrationModal.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { GoogleMap, PolygonF } from "@react-google-maps/api";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";

const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "560px",
  borderRadius: "8px",
};

/**
 * Custom OverlayView class for rendering rotated, semi-transparent ground images
 */
function createGroundOverlayClass() {
  if (!window.google || !window.google.maps || !window.google.maps.OverlayView) {
    return null;
  }

  return class RotatedGroundOverlay extends window.google.maps.OverlayView {
    constructor(bounds, image, rotation, opacity, map) {
      super();
      this.bounds = bounds;
      this.image = image;
      this.rotation = rotation || 0;
      this.opacity = opacity !== undefined ? opacity : 0.85;
      this.div = null;
      this.setMap(map);
    }

    onAdd() {
      this.div = document.createElement("div");
      this.div.style.borderStyle = "none";
      this.div.style.borderWidth = "0px";
      this.div.style.position = "absolute";
      this.div.style.pointerEvents = "none";
      this.div.style.zIndex = "10";

      const img = document.createElement("img");
      img.src = this.image;
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.position = "absolute";
      img.style.opacity = String(this.opacity);
      img.style.objectFit = "fill";
      this.div.appendChild(img);

      const panes = this.getPanes();
      panes.overlayLayer.appendChild(this.div);
    }

    draw() {
      const overlayProjection = this.getProjection();
      if (!overlayProjection || !this.div) return;

      const sw = overlayProjection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(this.bounds.south, this.bounds.west)
      );
      const ne = overlayProjection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(this.bounds.north, this.bounds.east)
      );

      if (sw && ne) {
        const width = ne.x - sw.x;
        const height = sw.y - ne.y;
        this.div.style.left = `${sw.x}px`;
        this.div.style.top = `${ne.y}px`;
        this.div.style.width = `${Math.max(width, 2)}px`;
        this.div.style.height = `${Math.max(height, 2)}px`;
        this.div.style.transform = `rotate(${this.rotation}deg)`;
        this.div.style.transformOrigin = "center center";

        const img = this.div.querySelector("img");
        if (img) img.style.opacity = String(this.opacity);
      }
    }

    update(bounds, rotation, opacity) {
      this.bounds = bounds;
      this.rotation = rotation;
      this.opacity = opacity;
      this.draw();
    }

    onRemove() {
      if (this.div && this.div.parentNode) {
        this.div.parentNode.removeChild(this.div);
        this.div = null;
      }
    }
  };
}

export default function FloorplanCalibrationModal({
  show,
  onClose,
  floorplan,
  locationId,
  buildingOutline,
  locationCenter,
  onSaveSuccess,
}) {
  const [map, setMap] = useState(null);
  const [mapTypeId, setMapTypeId] = useState("satellite");
  const [bounds, setBounds] = useState({
    north: floorplan?.north || 0,
    south: floorplan?.south || 0,
    east: floorplan?.east || 0,
    west: floorplan?.west || 0,
  });
  const [rotation, setRotation] = useState(floorplan?.rotation || 0);
  const [opacity, setOpacity] = useState(floorplan?.opacity !== undefined ? floorplan.opacity : 0.85);
  const [nudgeStep, setNudgeStep] = useState(0.00002); // ~2 meters
  const [saving, setSaving] = useState(false);

  const overlayRef = useRef(null);
  const rectRef = useRef(null);
  const isUpdatingFromMap = useRef(false);

  // Initialize bounds & rotation from floorplan props
  useEffect(() => {
    if (floorplan) {
      const initialBounds = {
        north: parseFloat(floorplan.north) || 0,
        south: parseFloat(floorplan.south) || 0,
        east: parseFloat(floorplan.east) || 0,
        west: parseFloat(floorplan.west) || 0,
      };
      setBounds(initialBounds);
      setRotation(parseFloat(floorplan.rotation) || 0);
      setOpacity(floorplan.opacity !== undefined ? parseFloat(floorplan.opacity) : 0.85);
    }
  }, [floorplan]);

  // Clean up overlays on unmount or close
  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
      if (rectRef.current) {
        rectRef.current.setMap(null);
        rectRef.current = null;
      }
    };
  }, []);

  // Update Custom Ground Overlay when bounds, rotation, or opacity change
  useEffect(() => {
    if (!map || !window.google || !floorplan?.image_url) return;

    if (!overlayRef.current) {
      const OverlayClass = createGroundOverlayClass();
      if (OverlayClass) {
        overlayRef.current = new OverlayClass(
          bounds,
          floorplan.image_url,
          rotation,
          opacity,
          map
        );
      }
    } else {
      overlayRef.current.update(bounds, rotation, opacity);
    }
  }, [map, bounds, rotation, opacity, floorplan]);

  // Sync Google Maps interactive Rectangle with bounds
  useEffect(() => {
    if (!map || !window.google) return;

    const gBounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(bounds.south, bounds.west),
      new window.google.maps.LatLng(bounds.north, bounds.east)
    );

    if (!rectRef.current) {
      rectRef.current = new window.google.maps.Rectangle({
        bounds: gBounds,
        editable: true,
        draggable: true,
        strokeColor: "#487fff",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: "#487fff",
        fillOpacity: 0.05,
        map,
        zIndex: 20,
      });

      const handleBoundsChanged = () => {
        if (!rectRef.current) return;
        const newGBounds = rectRef.current.getBounds();
        if (!newGBounds) return;

        isUpdatingFromMap.current = true;
        setBounds({
          north: newGBounds.getNorthEast().lat(),
          east: newGBounds.getNorthEast().lng(),
          south: newGBounds.getSouthWest().lat(),
          west: newGBounds.getSouthWest().lng(),
        });
        setTimeout(() => {
          isUpdatingFromMap.current = false;
        }, 50);
      };

      rectRef.current.addListener("bounds_changed", handleBoundsChanged);
    } else if (!isUpdatingFromMap.current) {
      rectRef.current.setBounds(gBounds);
    }
  }, [map, bounds]);

  // Center map on floorplan bounds on open
  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    if (bounds.north && bounds.south && bounds.east && bounds.west) {
      const centerLat = (bounds.north + bounds.south) / 2;
      const centerLng = (bounds.east + bounds.west) / 2;
      mapInstance.setCenter({ lat: centerLat, lng: centerLng });
      mapInstance.setZoom(19);
    }
  }, [bounds]);

  // Helper: Fit floorplan to the building's outline polygon
  const handleFitToOutline = () => {
    if (!buildingOutline || buildingOutline.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Building Outline Found",
        text: "Please draw the building outline in the Outlines segment first, or adjust coordinates manually.",
      });
      return;
    }

    const points = Array.isArray(buildingOutline[0]) ? buildingOutline.flat() : buildingOutline;
    const valid = points.filter((p) => p && typeof p.lat === "number" && typeof p.lng === "number");

    if (valid.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Outline",
        text: "No valid coordinates found in building outline.",
      });
      return;
    }

    const north = Math.max(...valid.map((p) => p.lat));
    const south = Math.min(...valid.map((p) => p.lat));
    const east = Math.max(...valid.map((p) => p.lng));
    const west = Math.min(...valid.map((p) => p.lng));

    setBounds({ north, south, east, west });

    if (map) {
      map.fitBounds(
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(south, west),
          new window.google.maps.LatLng(north, east)
        )
      );
    }
  };

  // Nudge controls
  const handleNudge = (direction) => {
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;

    let newNorth = bounds.north;
    let newSouth = bounds.south;
    let newEast = bounds.east;
    let newWest = bounds.west;

    if (direction === "N") {
      newNorth += nudgeStep;
      newSouth += nudgeStep;
    } else if (direction === "S") {
      newNorth -= nudgeStep;
      newSouth -= nudgeStep;
    } else if (direction === "E") {
      newEast += nudgeStep;
      newWest += nudgeStep;
    } else if (direction === "W") {
      newEast -= nudgeStep;
      newWest -= nudgeStep;
    } else if (direction === "EXPAND") {
      newNorth += latSpan * 0.02;
      newSouth -= latSpan * 0.02;
      newEast += lngSpan * 0.02;
      newWest -= lngSpan * 0.02;
    } else if (direction === "SHRINK") {
      newNorth -= latSpan * 0.02;
      newSouth += latSpan * 0.02;
      newEast -= lngSpan * 0.02;
      newWest += lngSpan * 0.02;
    }

    setBounds({ north: newNorth, south: newSouth, east: newEast, west: newWest });
  };

  // Dimensions in meters
  const widthMeters = Math.round(
    Math.abs(bounds.east - bounds.west) * 111320 * Math.cos(((bounds.north + bounds.south) / 2) * (Math.PI / 180))
  );
  const heightMeters = Math.round(Math.abs(bounds.north - bounds.south) * 110540);

  // Save changes to API
  const handleSave = async () => {
    if (!floorplan || !locationId) return;

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/main-locations/${locationId}/floorplans/${floorplan.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          north: bounds.north,
          south: bounds.south,
          east: bounds.east,
          west: bounds.west,
          rotation,
          opacity,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to update floorplan calibration.");
      }

      Swal.fire({
        icon: "success",
        title: "Calibration Saved!",
        text: `Floorplan for Level ${floorplan.level} coordinates have been saved.`,
        timer: 1800,
        showConfirmButton: false,
      });

      if (onSaveSuccess) onSaveSuccess(data.data);
      onClose();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!show || !floorplan) return null;

  const defaultCenter = locationCenter?.lat && locationCenter?.lng
    ? { lat: parseFloat(locationCenter.lat), lng: parseFloat(locationCenter.lng) }
    : { lat: 48.137154, lng: 11.576124 };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.65)", zIndex: 1050 }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content radius-16 border-0 shadow-lg overflow-hidden">
          {/* Header */}
          <div className="modal-header py-14 px-20 bg-neutral-50 border-bottom d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <div className="w-36-px h-36-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center flex-shrink-0">
                <Icon icon="solar:ruler-angular-bold" className="text-xl" />
              </div>
              <div>
                <h6 className="text-md fw-bold mb-0 text-dark">
                  Adjust Floorplan Coordinates — Level {floorplan.level}
                </h6>
                <p className="text-xs text-secondary-light mb-0">
                  Align and georeference the floorplan image over satellite imagery and building walls.
                </p>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 radius-8 px-12"
                onClick={handleFitToOutline}
                title="Fit bounding box automatically to the drawn building outline"
              >
                <Icon icon="solar:maximize-square-linear" className="text-sm" />
                Fit to Building Outline
              </button>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={saving}
              />
            </div>
          </div>

          {/* Modal Body */}
          <div className="modal-body p-16">
            <div className="row g-3">
              {/* Left / Main: Map Canvas */}
              <div className="col-lg-8">
                <div className="position-relative border radius-8 overflow-hidden">
                  <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={defaultCenter}
                    zoom={19}
                    mapTypeId={mapTypeId}
                    onLoad={onMapLoad}
                    options={{
                      tilt: 0,
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: true,
                    }}
                  >
                    {/* Render Building Outline Polygons if available */}
                    {buildingOutline && buildingOutline.length > 0 && (
                      <PolygonF
                        paths={Array.isArray(buildingOutline[0]) ? buildingOutline[0] : buildingOutline}
                        options={{
                          strokeColor: "#ff9f43",
                          strokeOpacity: 0.9,
                          strokeWeight: 2,
                          fillColor: "#ff9f43",
                          fillOpacity: 0.15,
                        }}
                      />
                    )}
                  </GoogleMap>

                  {/* Floating Map Controls */}
                  <div
                    className="position-absolute top-0 start-0 m-12 d-flex align-items-center gap-2 bg-white p-6 radius-8 shadow-sm border"
                    style={{ zIndex: 5 }}
                  >
                    <button
                      type="button"
                      className={`btn btn-xs py-1 px-8 radius-6 ${mapTypeId === "satellite" ? "btn-primary text-white" : "btn-light text-secondary-light"}`}
                      onClick={() => setMapTypeId("satellite")}
                    >
                      Satellite
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs py-1 px-8 radius-6 ${mapTypeId === "roadmap" ? "btn-primary text-white" : "btn-light text-secondary-light"}`}
                      onClick={() => setMapTypeId("roadmap")}
                    >
                      Map
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs py-1 px-8 radius-6 ${mapTypeId === "hybrid" ? "btn-primary text-white" : "btn-light text-secondary-light"}`}
                      onClick={() => setMapTypeId("hybrid")}
                    >
                      Hybrid
                    </button>
                  </div>

                  {/* Dimensions Badge Overlay */}
                  <div
                    className="position-absolute bottom-0 end-0 m-12 bg-neutral-900 bg-opacity-75 text-white px-10 py-4 radius-6 text-xs font-monospace shadow-sm"
                    style={{ zIndex: 5 }}
                  >
                    Size: ~{widthMeters}m × ~{heightMeters}m | Rot: {Math.round(rotation)}°
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-8 text-xxs text-secondary-light">
                  <span>💡 Tip: Drag the blue box edges or corner handles directly on the map to resize and position.</span>
                  <span className="badge bg-warning-50 text-warning-700 border border-warning-200">
                    Orange line = Building Outline
                  </span>
                </div>
              </div>

              {/* Right / Sidebar: Alignment & Precision Controls */}
              <div className="col-lg-4 d-flex flex-column gap-14">
                {/* Opacity Control */}
                <div className="p-12 radius-8 bg-neutral-50 border">
                  <div className="d-flex justify-content-between align-items-center mb-6">
                    <label className="form-label text-xs fw-semibold mb-0 text-dark">
                      Transparency / Opacity
                    </label>
                    <span className="badge bg-neutral-200 text-dark font-monospace text-xxs">
                      {Math.round(opacity * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    className="form-range"
                    min="0.1"
                    max="1.0"
                    step="0.05"
                    value={opacity}
                    onChange={(e) => setOpacity(parseFloat(e.target.value))}
                  />
                  <div className="d-flex justify-content-between text-xxs text-secondary-light">
                    <span>See satellite underneath (10%)</span>
                    <span>Solid plan (100%)</span>
                  </div>
                </div>

                {/* Rotation Control */}
                <div className="p-12 radius-8 bg-neutral-50 border">
                  <div className="d-flex justify-content-between align-items-center mb-6">
                    <label className="form-label text-xs fw-semibold mb-0 text-dark">
                      Rotation (Angle)
                    </label>
                    <span className="badge bg-primary-100 text-primary-700 font-monospace text-xxs">
                      {rotation}°
                    </span>
                  </div>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max="360"
                    step="1"
                    value={rotation}
                    onChange={(e) => setRotation(parseFloat(e.target.value))}
                  />
                  <div className="d-flex align-items-center justify-content-between gap-1 mt-6">
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-neutral radius-6 px-6"
                      onClick={() => setRotation((r) => ((r - 5 + 360) % 360))}
                    >
                      -5°
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-neutral radius-6 px-6"
                      onClick={() => setRotation((r) => ((r - 1 + 360) % 360))}
                    >
                      -1°
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-secondary radius-6 px-8 text-xxs"
                      onClick={() => setRotation(0)}
                    >
                      0° (North)
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-neutral radius-6 px-6"
                      onClick={() => setRotation((r) => ((r + 1) % 360))}
                    >
                      +1°
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-neutral radius-6 px-6"
                      onClick={() => setRotation((r) => ((r + 5) % 360))}
                    >
                      +5°
                    </button>
                  </div>
                </div>

                {/* Precision Nudge & Scale */}
                <div className="p-12 radius-8 bg-neutral-50 border">
                  <div className="d-flex justify-content-between align-items-center mb-10">
                    <label className="form-label text-xs fw-semibold mb-0 text-dark">
                      Precision Nudge
                    </label>
                    <select
                      className="form-select form-select-sm py-1 px-2 text-xxs radius-6"
                      style={{ width: "95px" }}
                      value={nudgeStep}
                      onChange={(e) => setNudgeStep(parseFloat(e.target.value))}
                    >
                      <option value="0.000005">Fine (0.5m)</option>
                      <option value="0.00002">Norm (2m)</option>
                      <option value="0.00008">Coarse (8m)</option>
                    </select>
                  </div>

                  {/* D-Pad Buttons */}
                  <div className="d-flex flex-column align-items-center gap-1 mb-8">
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-neutral radius-6 p-1 d-flex align-items-center justify-content-center"
                      style={{ width: "32px", height: "30px" }}
                      onClick={() => handleNudge("N")}
                      title="Nudge North"
                    >
                      <Icon icon="solar:arrow-up-linear" />
                    </button>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-neutral radius-6 p-1 d-flex align-items-center justify-content-center"
                        style={{ width: "32px", height: "30px" }}
                        onClick={() => handleNudge("W")}
                        title="Nudge West"
                      >
                        <Icon icon="solar:arrow-left-linear" />
                      </button>
                      <div
                        className="radius-6 bg-neutral-200 text-secondary-light text-xxs d-flex align-items-center justify-content-center"
                        style={{ width: "32px", height: "30px" }}
                      >
                        <Icon icon="solar:compass-linear" />
                      </div>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-neutral radius-6 p-1 d-flex align-items-center justify-content-center"
                        style={{ width: "32px", height: "30px" }}
                        onClick={() => handleNudge("E")}
                        title="Nudge East"
                      >
                        <Icon icon="solar:arrow-right-linear" />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-neutral radius-6 p-1 d-flex align-items-center justify-content-center"
                      style={{ width: "32px", height: "30px" }}
                      onClick={() => handleNudge("S")}
                      title="Nudge South"
                    >
                      <Icon icon="solar:arrow-down-linear" />
                    </button>
                  </div>

                  {/* Scale + / - Buttons */}
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-neutral radius-6 px-10 py-1 d-flex align-items-center gap-1 text-xxs"
                      onClick={() => handleNudge("SHRINK")}
                    >
                      <Icon icon="solar:minimize-square-linear" /> Shrink -2%
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-neutral radius-6 px-10 py-1 d-flex align-items-center gap-1 text-xxs"
                      onClick={() => handleNudge("EXPAND")}
                    >
                      <Icon icon="solar:maximize-square-linear" /> Expand +2%
                    </button>
                  </div>
                </div>

                {/* Live Coordinates Readout */}
                <div className="p-10 radius-8 bg-neutral-100 border text-xxs font-monospace text-secondary-light">
                  <div className="d-flex justify-content-between mb-1">
                    <span>NORTH:</span>
                    <span className="text-dark fw-bold">{bounds.north.toFixed(7)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>SOUTH:</span>
                    <span className="text-dark fw-bold">{bounds.south.toFixed(7)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>EAST:</span>
                    <span className="text-dark fw-bold">{bounds.east.toFixed(7)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>WEST:</span>
                    <span className="text-dark fw-bold">{bounds.west.toFixed(7)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer py-12 px-20 bg-neutral-50 border-top d-flex align-items-center justify-content-between">
            <span className="text-xs text-secondary-light">
              Press <strong>Save Alignment</strong> to store georeferenced coordinates to the database.
            </span>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-secondary radius-8 px-16 text-xs"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary radius-8 px-20 text-xs d-flex align-items-center gap-2"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon icon="solar:check-circle-bold" className="text-base" />
                    Save Alignment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

